import React from "react";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { FIELD_CONTROL, FIELD_LABEL, GhostButton, PrimaryButton, SelectField } from "../ui";
import StepIntro from "./StepIntro";
import { COMPANY_SIZES, Contact, PRIVACY_URL, ROLES, TERMS_URL } from "./calculator";

const TEXT_FIELDS: {
  name: "name" | "email" | "organization";
  label: string;
  type: string;
  autoComplete: string;
  placeholder: string;
  fullWidth?: boolean;
}[] = [
  { name: "name", label: "Full name", type: "text", autoComplete: "name", placeholder: "Jane Tan" },
  { name: "email", label: "Work email", type: "email", autoComplete: "email", placeholder: "jane@yourcompany.com" },
  {
    name: "organization",
    label: "Company name",
    type: "text",
    autoComplete: "organization",
    placeholder: "Acme Berhad",
    fullWidth: true,
  },
];

const asOptions = (values: readonly string[]) => values.map((value) => ({ value, label: value }));

type Props = {
  contact: Contact;
  onChange: (name: keyof Contact, value: string | boolean) => void;
  onBack: () => void;
  submitting: boolean;
  honeypot: string;
  onHoneypotChange: (value: string) => void;
};

export default function StepContact({
  contact,
  onChange,
  onBack,
  submitting,
  honeypot,
  onHoneypotChange,
}: Props) {
  return (
    <>
      <StepIntro
        eyebrow="2. Your details"
        title="Your business case is ready — tell us where to send it."
        copy="See your personalised result immediately after submitting. You can request an email copy on the next screen."
      />

      <div className="tw-mt-7 tw-grid tw-grid-cols-2 tw-gap-5 xs:tw-grid-cols-1">
        {TEXT_FIELDS.map((field) => {
          const id = `roi-contact-${field.name}`;

          return (
            <div
              key={field.name}
              className={`tw-min-w-0 ${field.fullWidth ? "tw-col-span-2 xs:tw-col-span-1" : ""}`}
            >
              <label htmlFor={id} className={FIELD_LABEL}>
                {field.label}
              </label>
              <input
                id={id}
                name={field.name}
                type={field.type}
                required
                autoComplete={field.autoComplete}
                placeholder={field.placeholder}
                value={contact[field.name]}
                onChange={(event) => onChange(field.name, event.target.value)}
                className={FIELD_CONTROL}
              />
            </div>
          );
        })}

        <SelectField
          id="roi-contact-company-size"
          name="company_size"
          label="Company size"
          placeholder="Select company size"
          value={contact.companySize}
          options={asOptions(COMPANY_SIZES)}
          onChange={(value) => onChange("companySize", value)}
        />

        <SelectField
          id="roi-contact-role"
          name="role"
          label="Role"
          placeholder="Select your role"
          value={contact.role}
          options={asOptions(ROLES)}
          onChange={(value) => onChange("role", value)}
        />
      </div>

      {/* Honeypot: hidden from people, tempting to bots. A filled value is rejected server-side. */}
      <div aria-hidden className="tw-absolute tw-left-[-9999px] tw-h-px tw-w-px tw-overflow-hidden">
        <label htmlFor="roi-website">Website</label>
        <input
          id="roi-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(event) => onHoneypotChange(event.target.value)}
        />
      </div>

      <div className="tw-mt-6 tw-rounded-2xl tw-border tw-border-[#c9deed] tw-bg-[#f4f9fd] tw-p-5">
        <label htmlFor="roi-contact-consent" className="tw-m-0 tw-flex tw-items-start tw-gap-3">
          <input
            id="roi-contact-consent"
            name="consent"
            type="checkbox"
            required
            checked={contact.consent}
            onChange={(event) => onChange("consent", event.target.checked)}
            className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-accent-[#087bd3]"
          />
          <span className="tw-text-sm tw-font-normal tw-leading-6 tw-text-[#304d68]">
            I agree that FutureLab may contact me about my estimate and mentoring programmes. See
            FutureLab&apos;s{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="tw-font-bold tw-text-[#087bd3] tw-underline"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="tw-font-bold tw-text-[#087bd3] tw-underline"
            >
              privacy policy
            </a>
            .
          </span>
        </label>
      </div>

      <div className="tw-mt-7 tw-flex tw-flex-row tw-items-center tw-gap-4 sm:tw-flex-col-reverse sm:tw-items-stretch">
        <GhostButton type="button" onClick={onBack} disabled={submitting}>
          <LuArrowLeft aria-hidden className="tw-h-4 tw-w-4" />
          Back
        </GhostButton>
        <PrimaryButton type="submit" disabled={submitting}>
          <LuArrowRight aria-hidden className="tw-h-4 tw-w-4" />
          {submitting ? "Building your business case…" : "See my business case"}
        </PrimaryButton>
      </div>
    </>
  );
}
